import React, { useCallback, useEffect, useState } from 'react';
import candidateWorkflowService, { ActivityEntry } from '../../../services/candidateWorkflow.service';
import CandidateActivityItem from './CandidateActivityItem';

/**
 * Zeitleiste eines Kandidaten. Lädt seitenweise nach (Regel 27) und bietet
 * den Eintrag der beiden häufigsten Vorgänge direkt an.
 */

interface Props {
  candidateId: string;
  aktualisierungsZaehler?: number;
  onGeaendert?: () => void;
}

const SCHNELLEINTRAEGE: Array<{ typ: string; label: string }> = [
  { typ: 'CALL_ATTEMPTED', label: 'Kontaktversuch' },
  { typ: 'CALL_CONNECTED', label: 'Telefonat' },
  { typ: 'NOTE_CREATED', label: 'Notiz' },
];

export const CandidateActivityTimeline: React.FC<Props> = ({
  candidateId, aktualisierungsZaehler = 0, onGeaendert,
}) => {
  const [eintraege, setEintraege] = useState<ActivityEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [notiz, setNotiz] = useState('');
  const [offenerTyp, setOffenerTyp] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async () => {
    try {
      setLaedt(true);
      const seite = await candidateWorkflowService.timeline(candidateId, { limit: 20 });
      setEintraege(seite.items);
      setCursor(seite.nextCursor);
      setFehler(null);
    } catch (e: any) {
      setFehler(e?.message || 'Der Verlauf konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [candidateId]);

  useEffect(() => { laden(); }, [laden, aktualisierungsZaehler]);

  const mehrLaden = async () => {
    if (!cursor) return;
    try {
      const seite = await candidateWorkflowService.timeline(candidateId, { limit: 20, before: cursor });
      setEintraege((bisher) => [...bisher, ...seite.items]);
      setCursor(seite.nextCursor);
    } catch (e: any) {
      setFehler(e?.message || 'Es konnte nicht weiter geladen werden.');
    }
  };

  const eintragen = async (typ: string) => {
    setFehler(null);
    try {
      setSpeichert(true);
      await candidateWorkflowService.protokolliere(candidateId, typ, notiz.trim() || undefined);
      setNotiz('');
      setOffenerTyp(null);
      await laden();
      onGeaendert?.();
    } catch (e: any) {
      setFehler(e?.message || 'Der Eintrag konnte nicht gespeichert werden.');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {SCHNELLEINTRAEGE.map((s) => (
          <button
            key={s.typ}
            className={offenerTyp === s.typ ? 'btn btn-primary' : 'btn btn-ghost'}
            style={{ fontSize: '0.78rem', padding: '4px 10px' }}
            onClick={() => setOffenerTyp(offenerTyp === s.typ ? null : s.typ)}
            disabled={speichert}
          >
            + {s.label}
          </button>
        ))}
      </div>

      {offenerTyp && (
        <div style={{ marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <textarea
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="Notiz (optional)"
            style={{
              flex: 1, minHeight: '54px', padding: '8px 10px', fontSize: '0.85rem',
              borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)',
              background: 'var(--glass)', color: 'var(--text-primary)', resize: 'vertical',
            }}
          />
          <button className="btn btn-primary" disabled={speichert} onClick={() => eintragen(offenerTyp)}>
            {speichert ? 'Speichert…' : 'Eintragen'}
          </button>
        </div>
      )}

      {fehler && <div style={{ color: '#EF4444', fontSize: '0.82rem', marginBottom: '10px' }}>{fehler}</div>}

      {laedt && eintraege.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Verlauf wird geladen…</div>
      ) : eintraege.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', padding: '8px 0' }}>
          Noch keine Aktivität.
        </div>
      ) : (
        <div>
          {eintraege.map((e) => <CandidateActivityItem key={e.id} eintrag={e} />)}
          {cursor && (
            <button className="btn btn-ghost" style={{ marginTop: '10px', fontSize: '0.8rem' }} onClick={mehrLaden}>
              Ältere anzeigen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CandidateActivityTimeline;
